/**
 * Browser Detection Utility
 * Detects in-app browsers and provides browser information
 * Helps identify problematic browsers like Instagram/Facebook in-app browsers
 */

export interface BrowserInfo {
  isInAppBrowser: boolean;
  browserName: string;
  browserVersion?: string;
  platform: string;
  userAgent: string;
  isSecureContext: boolean;
  hasServiceWorker: boolean;
  hasNotifications: boolean;
  hasStorage: boolean;
}

/**
 * Detect if the current browser is an in-app browser
 * @returns boolean indicating if running in an in-app browser
 */
export const isInAppBrowser = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const userAgent = window.navigator.userAgent.toLowerCase();
  
  // Common in-app browser signatures
  const inAppBrowserSignatures = [
    'fban',           // Facebook App
    'fbav',           // Facebook App
    'fb_iab',         // Facebook in-app browser
    'instagram',      // Instagram in-app browser
    'line',           // LINE in-app browser
    'micromessenger', // WeChat in-app browser
    '; qq',           // QQ in-app browser (bounded: bare 'qq' matches too much)
    'weibo',          // Weibo in-app browser
    'twitter',        // Twitter in-app browser
    'linkedin',       // LinkedIn in-app browser
    'whatsapp',       // WhatsApp in-app browser
    'telegram',       // Telegram in-app browser
    'snapchat',       // Snapchat in-app browser
    'tiktok',         // TikTok in-app browser
    'pinterest',      // Pinterest in-app browser
    'reddit',         // Reddit in-app browser
    'discord',        // Discord in-app browser
    'slack',          // Slack in-app browser
    'zoom',           // Zoom in-app browser
    'teams',          // Microsoft Teams in-app browser
    'webview',        // Generic webview
    '; wv',           // Android WebView (bounded: bare 'wv' matches too much)
  ];

  return inAppBrowserSignatures.some(signature => userAgent.includes(signature));
};

/**
 * Detect if the current browser is specifically Instagram or Facebook in-app browser
 * @returns boolean indicating if running in Instagram/Facebook in-app browser
 */
export const isInstagramOrFacebookInAppBrowser = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const userAgent = window.navigator.userAgent.toLowerCase();
  
  const problematicSignatures = [
    'fban',           // Facebook App
    'fbav',           // Facebook App
    'fb_iab',         // Facebook in-app browser
    'instagram',      // Instagram in-app browser
  ];

  return problematicSignatures.some(signature => userAgent.includes(signature));
};

/**
 * Get detailed browser information
 * @returns BrowserInfo object with browser details
 */
export const getBrowserInfo = (): BrowserInfo => {
  if (typeof window === 'undefined') {
    return {
      isInAppBrowser: false,
      browserName: 'server',
      platform: 'server',
      userAgent: '',
      isSecureContext: false,
      hasServiceWorker: false,
      hasNotifications: false,
      hasStorage: false,
    };
  }

  const rawUserAgent = window.navigator.userAgent;
  // Real user agents are capitalised ("Chrome/", "Safari/", "iPhone"). Comparing
  // the raw string against the lowercase literals below matched nothing, so
  // browserName was always 'unknown' for every visitor.
  const userAgent = rawUserAgent.toLowerCase();
  const isInApp = isInAppBrowser();

  // Detect browser name. Opera must be tested before Chrome: its token is "OPR",
  // and it also carries "Chrome" in the UA, so it would otherwise be misreported.
  let browserName = 'unknown';
  if (userAgent.includes('opr') || userAgent.includes('opera')) {
    browserName = 'opera';
  } else if (userAgent.includes('edg')) {
    browserName = 'edge';
  } else if (userAgent.includes('chrome')) {
    browserName = 'chrome';
  } else if (userAgent.includes('firefox')) {
    browserName = 'firefox';
  } else if (userAgent.includes('safari')) {
    browserName = 'safari';
  }

  // Detect platform
  let platform = 'unknown';
  if (userAgent.includes('android')) {
    platform = 'android';
  } else if (userAgent.includes('iphone') || userAgent.includes('ipad')) {
    platform = 'ios';
  } else if (userAgent.includes('windows')) {
    platform = 'windows';
  } else if (userAgent.includes('mac')) {
    platform = 'macos';
  } else if (userAgent.includes('linux')) {
    platform = 'linux';
  }

  return {
    isInAppBrowser: isInApp,
    browserName,
    platform,
    userAgent: rawUserAgent,
    isSecureContext: window.isSecureContext,
    hasServiceWorker: 'serviceWorker' in navigator,
    hasNotifications: 'Notification' in window,
    hasStorage: 'localStorage' in window && 'sessionStorage' in window,
  };
};

/**
 * Can this browser support push notifications and a service worker?
 *
 * Feature detection only. This replaced isProblematicBrowser(), which returned
 * true for every visitor because of the user-agent casing bug above, and which
 * conflated two unrelated questions: push capability and third-party script
 * safety. Analytics need neither a service worker nor the Notifications API.
 */
export const isPushCapableBrowser = (): boolean => {
  if (typeof window === 'undefined') return false;

  // Meta in-app WKWebViews expose no navigator.serviceWorker, and touching the
  // Firebase messaging SDK there throws during module evaluation. See CLAUDE.md.
  if (isInstagramOrFacebookInAppBrowser()) return false;

  if (!window.isSecureContext) return false;
  if (!('serviceWorker' in navigator)) return false;
  if (!('Notification' in window)) return false;
  if (!('PushManager' in window)) return false;

  return true;
};

/**
 * Is it safe to inject third-party vendor scripts here?
 *
 * Deliberately permissive: it only excludes environments where injecting vendor
 * scripts is known to break the page. Consent is a separate, independent gate —
 * see AnalyticsScripts.
 */
export const isThirdPartyScriptSafe = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (isInstagramOrFacebookInAppBrowser()) return false;
  if (!window.isSecureContext) return false;

  return true;
};

/**
 * Get browser capabilities
 * @returns object with browser capability flags
 */
export const getBrowserCapabilities = () => {
  if (typeof window === 'undefined') {
    return {
      hasLocalStorage: false,
      hasSessionStorage: false,
      hasServiceWorker: false,
      hasNotifications: false,
      hasPushManager: false,
      hasWebRTC: false,
      hasWebGL: false,
      hasWebAssembly: false,
    };
  }

  return {
    hasLocalStorage: 'localStorage' in window,
    hasSessionStorage: 'sessionStorage' in window,
    hasServiceWorker: 'serviceWorker' in navigator,
    hasNotifications: 'Notification' in window,
    hasPushManager: 'PushManager' in window,
    hasWebRTC: 'RTCPeerConnection' in window,
    hasWebGL: 'WebGLRenderingContext' in window,
    hasWebAssembly: 'WebAssembly' in window,
  };
};

/**
 * Get recommended action for the current browser
 * @returns string with recommended action
 */
export const getRecommendedAction = (): string => {
  const browserInfo = getBrowserInfo();
  
  if (isInstagramOrFacebookInAppBrowser()) {
    return 'open-in-browser';
  }
  
  if (!browserInfo.isSecureContext) {
    return 'use-https';
  }
  
  if (!browserInfo.hasStorage) {
    return 'enable-storage';
  }
  
  return 'none';
};

/**
 * Check if the browser supports all required features
 * @returns boolean indicating if all required features are supported
 */
export const hasRequiredFeatures = (): boolean => {
  const capabilities = getBrowserCapabilities();
  
  // Required features for the app
  const requiredFeatures = [
    'hasLocalStorage',
    'hasSessionStorage',
  ];
  
  return requiredFeatures.every(feature => capabilities[feature as keyof typeof capabilities]);
};
